<template>
  <b-overlay :show="loading" rounded="sm" opacity="0.25">
    <template #overlay>
      <div class="text-center p-3">
        <b-spinner variant="primary" class="mb-3" />

        <!-- 분리 다운로드: 이중 진행률 -->
        <template v-if="isSplitMode">
          <!-- 전체 진행률 -->
          <small class="text-muted d-block mb-1">
            {{ lang !== 'ENG' ? '전체 진행률' : 'Overall Progress' }}
          </small>
          <b-progress
            :max="100"
            :value="overallPercent"
            variant="info"
            animated
            class="mx-auto mb-2"
            style="width: 260px"
          />
          <small class="text-info font-weight-bold d-block mb-3">{{ overallPercent }}%</small>

          <!-- 현재 파일 진행률 -->
          <small class="text-muted d-block mb-1">
            {{ splitFileLabel }} ({{ currentStep }}/{{ totalSteps }})
          </small>
          <b-progress
            :max="splitTotalSteps"
            :value="splitCurrentStep"
            variant="primary"
            animated
            class="mx-auto"
            style="width: 260px"
          />
          <p class="mt-1 mb-0 small">{{ progressStep }}</p>
          <small class="text-muted">{{ splitPercent }}%</small>
        </template>

        <!-- 전체 다운로드: 단일 진행률 -->
        <template v-else>
          <b-progress
            :max="totalSteps"
            :value="currentStep"
            variant="primary"
            animated
            class="mx-auto"
            style="width: 260px"
          />
          <p class="mt-2 mb-0 font-weight-bold">{{ progressStep }}</p>
          <small class="text-muted">{{ singlePercent }}%</small>
        </template>

        <div v-if="isSplitMode" class="mt-3">
          <b-button size="sm" variant="outline-danger" @click="cancelSplitDownload">
            {{ lang !== 'ENG' ? '중단' : 'Stop' }}
          </b-button>
        </div>
      </div>
    </template>

    <!-- 헤더 영역 -->
    <div class="print-option-header">
      <p class="mb-2 text-secondary">
        {{ lang !== 'ENG' ? '다운로드할 목차를 선택해주세요.' : 'Select chapters to download.' }}
      </p>
      <div class="d-flex align-items-center justify-content-between">
        <b-form-checkbox
          :checked="allSelected"
          :indeterminate="someSelected && !allSelected"
          class="select-all-checkbox"
          @change="toggleAll"
        >
          {{ lang !== 'ENG' ? '전체 선택' : 'Select All' }}
        </b-form-checkbox>
        <b-badge variant="light" class="selection-badge">
          {{ selectedLv2Count }} / {{ totalLv2Count }}
        </b-badge>
      </div>
    </div>

    <!-- LV1 → LV2 트리 -->
    <div class="lv-tree-container">
      <div
        v-for="(lv1_info, lv1_idx) in sb_toc"
        :key="lv1_idx"
        class="lv1-group"
        :class="{ 'lv1-group--expanded': expandedLv1[lv1_info.key] }"
      >
        <!-- LV1 헤더 -->
        <div class="lv1-header" @click="toggleCollapse(lv1_info.key)">
          <div class="d-flex align-items-center" @click.stop>
            <b-form-checkbox
              :checked="isLv1AllSelected(lv1_info)"
              :indeterminate="isLv1Indeterminate(lv1_info)"
              class="lv1-checkbox"
              @change="toggleLv1(lv1_info, $event)"
            >
              <span class="lv1-name">{{ lv1_info.name }}</span>
            </b-form-checkbox>
          </div>
          <div class="d-flex align-items-center">
            <span v-if="selectedLv2Map[lv1_info.key]" class="lv1-count">
              {{ selectedLv2Map[lv1_info.key].length }}/{{ lv1_info.children.length }}
            </span>
            <span class="lv1-chevron" :class="{ 'lv1-chevron--open': expandedLv1[lv1_info.key] }">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  fill="none"
                />
              </svg>
            </span>
          </div>
        </div>

        <!-- LV2 목록 -->
        <b-collapse :visible="!!expandedLv1[lv1_info.key]">
          <div class="lv2-list">
            <label
              v-for="(lv2_info, lv2_idx) in lv1_info.children"
              :key="lv2_idx"
              class="lv2-item"
              :class="{ 'lv2-item--checked': isLv2Selected(lv1_info.key, lv2_info.key) }"
            >
              <b-form-checkbox
                :checked="isLv2Selected(lv1_info.key, lv2_info.key)"
                class="lv2-checkbox"
                @change="toggleLv2(lv1_info.key, lv2_info.key, $event)"
              >
                {{ lv2_info.name }}
              </b-form-checkbox>
            </label>
          </div>
        </b-collapse>
      </div>
    </div>

    <!-- 하단 액션 -->
    <b-form-invalid-feedback :state="state">
      {{
        lang !== 'ENG' ? '목차를 하나 이상 선택해주세요!' : 'Please select at least one chapter!'
      }}
    </b-form-invalid-feedback>

    <!-- 가이드 -->
    <div class="download-guide">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#868e96" style="vertical-align: -1px">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1 1 0 110 2 1 1 0 010-2zM7 7h2v5H7V7z" />
      </svg>
      <span>{{
        lang !== 'ENG'
          ? '전체 다운로드는 하나의 PDF로, 분리 다운로드는 항목별로 각각 PDF 파일을 생성합니다.'
          : 'Full download creates one PDF. Split download creates separate PDFs per item.'
      }}</span>
    </div>

    <div class="print-option-footer">
      <b-dropdown :disabled="!state" variant="teal" class="btn-download-dropdown" right>
        <template #button-content>
          <svg
            class="mr-1"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style="vertical-align: -2px"
          >
            <path
              d="M8 1v9m0 0L5 7m3 3l3-3M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          PDF {{ lang !== 'ENG' ? '다운로드' : 'Download' }}
        </template>

        <b-dropdown-item @click="handleDownload">
          <div class="dropdown-item-label">
            {{ lang !== 'ENG' ? '전체 다운로드' : 'Full Download' }}
          </div>
          <small class="text-muted">{{
            lang !== 'ENG'
              ? '선택된 항목 전체를 하나의 PDF로 다운로드'
              : 'Download all selected items as one PDF'
          }}</small>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item
          :disabled="!canSplitByLv1"
          :class="{ 'dropdown-item--disabled': !canSplitByLv1 }"
          @click="handleSplitDownload('lv1')"
        >
          <div class="dropdown-item-label">
            {{ lang !== 'ENG' ? '대주제별 분리 다운로드' : 'Split by Chapter' }}
          </div>
          <small class="text-muted">{{
            canSplitByLv1
              ? lv1SplitHint
              : lang !== 'ENG'
              ? '대주제를 2개 이상 선택해주세요'
              : 'Select 2+ chapters'
          }}</small>
          <small v-if="canSplitByLv1" class="text-muted d-block">{{
            lang !== 'ENG'
              ? `선택된 대주제별로 PDF ${filteredPrintOption.length}개 생성`
              : `Creates ${filteredPrintOption.length} PDF files from selected chapters`
          }}</small>
        </b-dropdown-item>

        <b-dropdown-item
          :disabled="!canSplitByLv2"
          :class="{ 'dropdown-item--disabled': !canSplitByLv2 }"
          @click="handleSplitDownload('lv2')"
        >
          <div class="dropdown-item-label">
            {{ lang !== 'ENG' ? '소주제별 분리 다운로드' : 'Split by Sub-topic' }}
          </div>
          <small class="text-muted">{{
            canSplitByLv2
              ? lv2SplitHint
              : lang !== 'ENG'
              ? '소주제를 2개 이상 선택해주세요'
              : 'Select 2+ sub-topics'
          }}</small>
          <small v-if="canSplitByLv2" class="text-muted d-block">{{
            lang !== 'ENG'
              ? `선택된 소주제별로 PDF ${selectedLv2Count}개 생성`
              : `Creates ${selectedLv2Count} PDF files from selected sub-topics`
          }}</small>
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </b-overlay>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import PrintSb from '@/plugins/print_plugin/printareaSb.js';

export default {
  name: 'SbPrintOption',
  data() {
    return {
      selectedLv2Map: {},
      expandedLv1: {},
      isSplitMode: false,
      loading: false,
      closeBtn: true,
      progressStep: '',
      currentStep: 0,
      totalSteps: 5,
      splitCurrentStep: 0,
      splitTotalSteps: 5,
      splitFileLabel: ''
    };
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapGetters('story_board', ['sb_toc']),
    ...mapState('story_board', [
      'selectedLv1PrintOption',
      'viewer_sb_toc',
      'editor_sb_toc',
      'viewer_disclosure_map',
      'print_mode'
    ]),

    state() {
      return Object.values(this.selectedLv2Map).some(keys => keys.length > 0);
    },

    allSelected() {
      return this.sb_toc.every(lv1 => this.selectedLv2Map[lv1.key]?.length === lv1.children.length);
    },

    someSelected() {
      return this.state;
    },

    selectedLv2Count() {
      return Object.values(this.selectedLv2Map).reduce((sum, keys) => sum + keys.length, 0);
    },

    totalLv2Count() {
      return this.sb_toc.reduce((sum, lv1) => sum + lv1.children.length, 0);
    },

    filteredPrintOption() {
      return this.sb_toc
        .filter(lv1 => this.selectedLv2Map[lv1.key]?.length > 0)
        .map(lv1 => ({
          ...lv1,
          children: lv1.children.filter(lv2 => this.selectedLv2Map[lv1.key]?.includes(lv2.key))
        }));
    },

    canSplitByLv1() {
      return this.filteredPrintOption.length >= 2;
    },

    canSplitByLv2() {
      return this.selectedLv2Count >= 2;
    },

    overallPercent() {
      if (this.totalSteps === 0) return 0;
      const fileProgress = this.splitTotalSteps ? this.splitCurrentStep / this.splitTotalSteps : 0;
      return Math.max(
        0,
        Math.round(((this.currentStep - 1 + fileProgress) / this.totalSteps) * 100)
      );
    },

    splitPercent() {
      return this.splitTotalSteps
        ? Math.round((this.splitCurrentStep / this.splitTotalSteps) * 100)
        : 0;
    },

    singlePercent() {
      return this.totalSteps ? Math.round((this.currentStep / this.totalSteps) * 100) : 0;
    },

    lv1SplitHint() {
      const isEng = this.lang === 'ENG';
      const selected = this.filteredPrintOption;
      const names = selected
        .slice(0, 3)
        .map(lv1 => (isEng ? lv1.name_em || lv1.name_ea || lv1.name : lv1.name));
      const suffix = selected.length > 3 ? ' ...' : '';
      return names.join(', ') + suffix;
    },

    lv2SplitHint() {
      const isEng = this.lang === 'ENG';
      const names = [];
      for (const lv1 of this.filteredPrintOption) {
        for (const lv2 of lv1.children) {
          names.push(isEng ? lv2.name_em || lv2.name_ea || lv2.name : lv2.name);
          if (names.length >= 3) break;
        }
        if (names.length >= 3) break;
      }
      const suffix = this.selectedLv2Count > 3 ? ' ...' : '';
      return names.join(', ') + suffix;
    },

    baseReportTitle() {
      return this.lang === 'KOR'
        ? this.selected_report.report_name
        : this.selected_report.report_name_e;
    },

    popTitle() {
      if (this.filteredPrintOption.length === 1) {
        const lv1 = this.filteredPrintOption[0];
        if (lv1.children.length === 1) {
          return `${this.baseReportTitle}_${lv1.name}_${lv1.children[0].name}`;
        }
        return `${this.baseReportTitle}_${lv1.name}`;
      }
      return this.baseReportTitle;
    }
  },

  watch: {
    sb_toc: {
      immediate: true,
      handler(toc) {
        if (!toc) return;
        const map = {};
        for (const lv1 of toc) {
          this.$set(
            map,
            lv1.key,
            lv1.children.map(lv2 => lv2.key)
          );
        }
        this.selectedLv2Map = map;
      }
    }
  },

  mounted() {
    this.$root.$on('bv::modal::hide', this.onModalHide);
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::hide', this.onModalHide);
    this.resetState();
  },
  methods: {
    ...mapMutations('story_board', ['update_print_option']),

    onModalHide(bvEvent, modalId) {
      if (modalId === 'story-board-modal') {
        this.resetState();
      }
    },

    isLv1AllSelected(lv1) {
      return this.selectedLv2Map[lv1.key]?.length === lv1.children.length;
    },

    isLv1Indeterminate(lv1) {
      const selected = this.selectedLv2Map[lv1.key]?.length || 0;
      return selected > 0 && selected < lv1.children.length;
    },

    isLv2Selected(lv1Key, lv2Key) {
      return this.selectedLv2Map[lv1Key]?.includes(lv2Key) || false;
    },

    toggleAll(checked) {
      const map = {};
      for (const lv1 of this.sb_toc) {
        this.$set(map, lv1.key, checked ? lv1.children.map(lv2 => lv2.key) : []);
      }
      this.selectedLv2Map = map;
    },

    toggleLv1(lv1, checked) {
      this.$set(this.selectedLv2Map, lv1.key, checked ? lv1.children.map(lv2 => lv2.key) : []);
    },

    toggleCollapse(lv1Key) {
      this.$set(this.expandedLv1, lv1Key, !this.expandedLv1[lv1Key]);
    },

    toggleLv2(lv1Key, lv2Key, checked) {
      const current = [...(this.selectedLv2Map[lv1Key] || [])];
      if (checked) {
        if (!current.includes(lv2Key)) current.push(lv2Key);
      } else {
        const idx = current.indexOf(lv2Key);
        if (idx !== -1) current.splice(idx, 1);
      }
      this.$set(this.selectedLv2Map, lv1Key, current);
    },

    cleanupOrphanedIframes() {
      document.querySelectorAll('iframe[id^="printArea_"]').forEach(iframe => {
        try {
          iframe.remove();
        } catch (_e) {
          // 이미 제거된 경우 무시
        }
      });
    },

    resetState() {
      this.loading = false;
      this.isSplitMode = false;
      this.closeBtn = true;
      this.progressStep = '';
      this.currentStep = 0;
      this.totalSteps = 5;
      this.splitCurrentStep = 0;
      this.splitTotalSteps = 5;
      this.splitFileLabel = '';
      this.cleanupOrphanedIframes();
      this.$store.commit('story_board/update_print_mode', false);
    },

    cancelSplitDownload() {
      this.resetState();
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    restorePrintArea() {
      // _cleanup()이 부모에 d-none을 추가하므로, 다음 반복 전에 복원
      const el = document.querySelector('#full-report-preview-sb');
      if (el && el.parentElement) {
        el.parentElement.classList.remove('d-none');
        return;
      }
      // print_mode가 false로 바뀌어 DOM에서 제거된 경우 복원
      this.$store.commit('story_board/update_print_mode', true);
    },

    buildSplitTargets(splitMode) {
      // lv1 모드: 대목차별 분리 — 각 LV1이 하나의 PDF
      // lv2 모드: 소주제별 분리 — 각 LV2가 하나의 PDF
      if (splitMode === 'lv1') {
        return this.filteredPrintOption.map(lv1 => ({
          label: lv1.name,
          title:
            lv1.children.length === 1
              ? `${this.baseReportTitle}_${lv1.name}_${lv1.children[0].name}`
              : `${this.baseReportTitle}_${lv1.name}`,
          printOption: [lv1]
        }));
      }
      // lv2 모드
      const targets = [];
      for (const lv1 of this.filteredPrintOption) {
        for (const lv2 of lv1.children) {
          targets.push({
            label: `${lv1.name} - ${lv2.name}`,
            title: `${this.baseReportTitle}_${lv1.name}_${lv2.name}`,
            printOption: [{ ...lv1, children: [lv2] }]
          });
        }
      }
      return targets;
    },

    finishSplitDownload(autoClose) {
      this.resetState();
      if (autoClose) {
        this.$bvModal.hide('story-board-modal');
      }
    },

    async handleSplitDownload(splitMode) {
      if (splitMode === 'lv1' && !this.canSplitByLv1) return;
      if (splitMode === 'lv2' && !this.canSplitByLv2) return;

      const isEng = this.lang === 'ENG';
      const targets = this.buildSplitTargets(splitMode);
      const total = targets.length;

      this.loading = true;
      this.isSplitMode = true;
      this.totalSteps = total;
      this.currentStep = 0;
      this.$store.commit('story_board/update_print_mode', true);

      for (let i = 0; i < total; i++) {
        if (!this.loading) return this.finishSplitDownload(false);

        const { label, title, printOption } = targets[i];
        this.currentStep = i + 1;
        this.splitFileLabel = label;
        this.splitCurrentStep = 0;

        // Step 1: 콘텐츠 준비
        this.splitCurrentStep = 1;
        this.progressStep = isEng ? 'Preparing content...' : '콘텐츠 준비 중...';
        this.update_print_option(printOption);
        this.restorePrintArea();
        await this.$nextTick();
        await this.$nextTick();
        await this.delay(500);

        if (!this.loading) return this.finishSplitDownload(false);

        // Step 2: DOM 렌더링 대기
        this.splitCurrentStep = 2;
        this.progressStep = isEng ? 'Rendering pages...' : '페이지 렌더링 중...';
        await this.waitForPrintElement();

        if (!this.loading) return this.finishSplitDownload(false);

        // Step 3: 이미지 로딩 대기
        this.splitCurrentStep = 3;
        this.progressStep = isEng ? 'Loading images...' : '이미지 로딩 중...';
        await this.waitForImages();

        if (!this.loading) return this.finishSplitDownload(false);

        // Step 4: PDF 생성
        this.splitCurrentStep = 4;
        this.progressStep = isEng ? 'Creating PDF...' : 'PDF 생성 중...';

        await new Promise(resolve => {
          new PrintSb({
            ids: '#full-report-preview-sb',
            popTitle: title,
            selectedLv1PrintOption: printOption,
            endCallback: () => {
              resolve();
            }
          });
        });

        // Step 5: 완료
        this.splitCurrentStep = 5;
        await this.delay(300);
      }

      // 전체 완료 — 모달 자동 닫기
      this.finishSplitDownload(true);
    },

    async handleDownload() {
      const isEng = this.lang === 'ENG';
      this.loading = true;
      this.currentStep = 0;
      this.$store.commit('story_board/update_print_mode', true);

      // Step 1: 콘텐츠 준비
      this.currentStep = 1;
      this.progressStep = isEng ? '1. Preparing content...' : '1. 콘텐츠 준비 중...';
      this.update_print_option(this.filteredPrintOption);
      await this.$nextTick();
      await this.delay(1000);

      if (!this.loading) return;

      // Step 2: DOM 렌더링 대기
      this.currentStep = 2;
      this.progressStep = isEng ? '2. Rendering pages...' : '2. 페이지 렌더링 중...';
      await this.waitForPrintElement();
      await this.delay(1000);

      if (!this.loading) return;

      // Step 3: 이미지 로딩 대기
      this.currentStep = 3;
      this.progressStep = isEng ? '3. Loading images...' : '3. 이미지 로딩 중...';
      await this.waitForImages();
      await this.delay(1000);

      if (!this.loading) return;

      // Step 4: PDF 문서 생성
      this.currentStep = 4;
      this.progressStep = isEng ? '4. Creating PDF document...' : '4. PDF 문서 생성 중...';
      await this.delay(1000);

      if (!this.loading) return;

      if (this.closeBtn) {
        this.closeBtn = false;

        // Step 5: 인쇄 대화상자 열기
        this.currentStep = 5;
        this.progressStep = isEng ? '5. Opening print dialog...' : '5. 인쇄 대화상자 열기...';

        new PrintSb({
          ids: '#full-report-preview-sb',
          popTitle: this.popTitle,
          selectedLv1PrintOption: this.filteredPrintOption,
          endCallback: () => {
            this.resetState();
            this.$bvModal.hide('story-board-modal');
          }
        });
      }
    },

    waitForPrintElement() {
      return new Promise(resolve => {
        const checkInterval = 300;
        const maxWaitTime = 10000;
        let elapsedTime = 0;

        const intervalId = setInterval(() => {
          if (!this.loading) {
            clearInterval(intervalId);
            resolve();
            return;
          }

          const printElement = document.querySelector('#full-report-preview-sb');
          if (printElement || elapsedTime >= maxWaitTime) {
            clearInterval(intervalId);
            resolve();
          }
          elapsedTime += checkInterval;
        }, checkInterval);
      });
    },

    waitForImages() {
      return new Promise(resolve => {
        const checkInterval = 500;
        const maxWaitTime = 30000;
        let elapsedTime = 0;

        const intervalId = setInterval(() => {
          if (!this.loading) {
            clearInterval(intervalId);
            resolve();
            return;
          }

          const printElement = document.querySelector('#full-report-preview-sb');
          if (!printElement) {
            if (elapsedTime >= maxWaitTime) {
              clearInterval(intervalId);
              resolve();
            }
            elapsedTime += checkInterval;
            return;
          }

          const images = Array.from(printElement.querySelectorAll('img'));
          const allLoaded = images.length === 0 || images.every(img => img.complete);

          if (allLoaded || elapsedTime >= maxWaitTime) {
            clearInterval(intervalId);
            // 렌더링 안정화를 위해 1초 추가 대기
            setTimeout(resolve, 1000);
          }

          elapsedTime += checkInterval;
        }, checkInterval);
      });
    }
  }
};
</script>

<style scoped>
/* 헤더 */
.print-option-header {
  margin-bottom: 12px;
}

.select-all-checkbox {
  font-weight: 600;
  font-size: 14px;
}

.selection-badge {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  background-color: #f1f3f5;
  border: 1px solid #dee2e6;
  padding: 3px 10px;
  border-radius: 12px;
}

/* 트리 컨테이너 */
.lv-tree-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 4px;
}

.lv-tree-container::-webkit-scrollbar {
  width: 5px;
}

.lv-tree-container::-webkit-scrollbar-thumb {
  background-color: #ced4da;
  border-radius: 4px;
}

/* LV1 그룹 */
.lv1-group {
  border-radius: 6px;
  overflow: hidden;
  transition: background-color 0.15s;
}

.lv1-group + .lv1-group {
  margin-top: 2px;
}

.lv1-group--expanded {
  background-color: #f8f9fa;
}

/* LV1 헤더 */
.lv1-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s;
  user-select: none;
}

.lv1-header:hover {
  background-color: #f1f3f5;
}

.lv1-checkbox {
  margin-bottom: 0;
}

.lv1-name {
  font-weight: 600;
  font-size: 14px;
  color: #343a40;
}

.lv1-count {
  font-size: 11px;
  color: #868e96;
  margin-right: 6px;
  font-variant-numeric: tabular-nums;
}

/* LV1 chevron */
.lv1-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #adb5bd;
  transition: transform 0.2s ease;
}

.lv1-chevron--open {
  transform: rotate(180deg);
}

/* LV2 목록 */
.lv2-list {
  padding: 2px 8px 6px 28px;
}

.lv2-item {
  display: block;
  padding: 4px 8px;
  margin: 1px 0;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.1s;
}

.lv2-item:hover {
  background-color: #e9ecef;
}

.lv2-item--checked {
  background-color: #e7f5ff;
}

.lv2-item--checked:hover {
  background-color: #d0ebff;
}

.lv2-checkbox {
  margin-bottom: 0;
  font-size: 13px;
  color: #495057;
}

/* 가이드 */
.download-guide {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  color: #868e96;
  line-height: 1.5;
}

.download-guide svg {
  flex-shrink: 0;
  margin-top: 2px;
}

/* 하단 버튼 */
.print-option-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.dropdown-item-label {
  font-weight: 500;
  font-size: 13px;
  color: #343a40;
}
</style>

<style>
/* 모달이 열렸을 때 백그라운드 스크롤 방지 (전역 스타일) */
body.modal-open {
  overflow: hidden !important;
  position: fixed !important;
  width: 100% !important;
}

/* 비활성화된 드롭다운 아이템 시각 구분 */
.dropdown-item--disabled .dropdown-item {
  opacity: 0.45;
  cursor: not-allowed;
}

.dropdown-item--disabled .dropdown-item-label {
  color: #adb5bd;
}
</style>
